import { skipToken, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { trpc, useTRPC } from "../../../shared/api/trpc";

export type SprintTrackView = Awaited<ReturnType<typeof trpc.sprints.list.query>>;
export type Sprint = SprintTrackView["sprints"][number];
export type SprintItem = SprintTrackView["items"][number];
export type SprintEstimateSource = SprintTrackView["estimateSources"][number];

export type SprintState = "active" | "closed" | "overdue" | "planned";

// A sprint runs because someone started it, not because a date passed. Dates
// only separate a running sprint from one that has overrun its window.
export function sprintStateFor(
  sprint: Pick<Sprint, "closedAt" | "endsAt" | "startedAt">,
  todayIsoDate = todayIso(),
): SprintState {
  if (sprint.closedAt) return "closed";
  if (!sprint.startedAt) return "planned";
  if (todayIsoDate > sprint.endsAt) return "overdue";
  return "active";
}

export function todayIso() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
}

export type SprintMoveInput = {
  position?: { nextWorkObjectId: string | null; previousWorkObjectId: string | null } | null;
  sprintId: string | null;
  workObjectId: string;
};

// Shared by the cache patch and the planning surface's synchronous override,
// so the optimistic order is identical everywhere.
export function applySprintMove(items: SprintItem[], input: SprintMoveInput): SprintItem[] {
  const moved = items.find((item) => item.workObject.id === input.workObjectId);
  if (!moved) return items;
  const next = items.filter((item) => item.workObject.id !== input.workObjectId);
  const updated = { ...moved, sprintId: input.sprintId };
  const anchorId = input.position?.previousWorkObjectId ?? null;
  const beforeId = input.position?.nextWorkObjectId ?? null;
  const anchorIndex = anchorId ? next.findIndex((item) => item.workObject.id === anchorId) : -1;
  const beforeIndex = beforeId ? next.findIndex((item) => item.workObject.id === beforeId) : -1;
  if (anchorIndex !== -1) next.splice(anchorIndex + 1, 0, updated);
  else if (beforeIndex !== -1) next.splice(beforeIndex, 0, updated);
  else next.push(updated);
  return next;
}

export function useSprints(boardId: string | null) {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const listQuery = useQuery(trpc.sprints.list.queryOptions(boardId ? { boardId } : skipToken));
  const createMutation = useMutation(trpc.sprints.createSprint.mutationOptions());
  const updateMutation = useMutation(trpc.sprints.updateSprint.mutationOptions());
  const archiveMutation = useMutation(trpc.sprints.archiveSprint.mutationOptions());
  const closeMutation = useMutation(trpc.sprints.closeSprint.mutationOptions());
  const startMutation = useMutation(trpc.sprints.startSprint.mutationOptions());
  const revertStartMutation = useMutation(trpc.sprints.revertSprintStart.mutationOptions());
  const addItemsMutation = useMutation(trpc.sprints.addItems.mutationOptions());
  const moveItemMutation = useMutation(trpc.sprints.moveItem.mutationOptions());
  const removeItemMutation = useMutation(trpc.sprints.removeItem.mutationOptions());
  const updateCadenceMutation = useMutation(trpc.sprints.updateCadence.mutationOptions());
  const addSourceMutation = useMutation(trpc.sprints.addEstimateSource.mutationOptions());
  const updateSourceMutation = useMutation(trpc.sprints.updateEstimateSource.mutationOptions());
  const removeSourceMutation = useMutation(trpc.sprints.removeEstimateSource.mutationOptions());

  async function reload() {
    if (!boardId) return;
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: trpc.sprints.list.queryKey({ boardId }) }),
      queryClient.invalidateQueries({ queryKey: trpc.boards.readViewQuery.queryKey() }),
    ]);
  }

  function patchItems(mutate: (items: SprintItem[]) => SprintItem[]) {
    if (!boardId) return;
    queryClient.setQueryData(trpc.sprints.list.queryKey({ boardId }), (current) =>
      current ? { ...current, items: mutate(current.items) } : current,
    );
  }

  async function createSprint(input: { endsAt: string; goal?: string; name?: string; startsAt: string }) {
    if (!boardId) throw new Error("Sprint board is not selected");
    const sprint = await createMutation.mutateAsync({ ...input, boardId });
    await reload();
    return sprint;
  }

  async function updateSprint(input: { endsAt?: string; goal?: string; name?: string; sprintId: string; startsAt?: string }) {
    const sprint = await updateMutation.mutateAsync(input);
    await reload();
    return sprint;
  }

  async function archiveSprint(sprintId: string) {
    await archiveMutation.mutateAsync({ sprintId });
    await reload();
  }

  async function closeSprint(input: { carryTarget: string; reason?: string; sprintId: string }) {
    await closeMutation.mutateAsync(input);
    await reload();
  }

  async function startSprint(input: { adjustDates: boolean; sprintId: string }) {
    const sprint = await startMutation.mutateAsync(input);
    await reload();
    return sprint;
  }

  // Administrative undo of a start: the sprint goes back to planned and its
  // commitment baseline is dropped.
  async function revertSprintStart(input: { endsAt?: string; sprintId: string; startsAt?: string }) {
    const sprint = await revertStartMutation.mutateAsync(input);
    await reload();
    return sprint;
  }

  async function addEstimateSource(input: { fieldDefinitionId: string; optionValues?: Record<string, number> | null }) {
    if (!boardId) throw new Error("Sprint board is not selected");
    await addSourceMutation.mutateAsync({ ...input, boardId });
    await Promise.all([reload(), queryClient.invalidateQueries({ queryKey: trpc.sprints.report.queryKey() })]);
  }

  async function updateEstimateSource(input: { optionValues: Record<string, number> | null; sourceId: string }) {
    await updateSourceMutation.mutateAsync(input);
    await Promise.all([reload(), queryClient.invalidateQueries({ queryKey: trpc.sprints.report.queryKey() })]);
  }

  async function removeEstimateSource(sourceId: string) {
    await removeSourceMutation.mutateAsync({ sourceId });
    await Promise.all([reload(), queryClient.invalidateQueries({ queryKey: trpc.sprints.report.queryKey() })]);
  }

  async function addItems(input: { sprintId: string | null; workObjectIds: string[] }) {
    if (!boardId) throw new Error("Sprint board is not selected");
    await addItemsMutation.mutateAsync({ ...input, boardId });
    await reload();
  }

  async function moveItem(input: SprintMoveInput) {
    if (!boardId) throw new Error("Sprint board is not selected");
    patchItems((items) => applySprintMove(items, input));
    try {
      await moveItemMutation.mutateAsync({ ...input, boardId });
    } finally {
      await reload();
    }
  }

  async function updateCadence(cadenceWeeks: number | null) {
    if (!boardId) throw new Error("Sprint board is not selected");
    await updateCadenceMutation.mutateAsync({ boardId, cadenceWeeks });
    await reload();
  }

  async function removeItem(workObjectId: string) {
    if (!boardId) throw new Error("Sprint board is not selected");
    patchItems((items) => items.filter((item) => item.workObject.id !== workObjectId));
    try {
      await removeItemMutation.mutateAsync({ boardId, workObjectId });
    } finally {
      await reload();
    }
  }

  return {
    addEstimateSource,
    addItems,
    archiveSprint,
    closeSprint,
    revertSprintStart,
    startSprint,
    createSprint,
    removeEstimateSource,
    updateEstimateSource,
    data: listQuery.data ?? null,
    isLoading: listQuery.isLoading,
    isMutating:
      addItemsMutation.isPending ||
      archiveMutation.isPending ||
      closeMutation.isPending ||
      createMutation.isPending ||
      moveItemMutation.isPending ||
      removeItemMutation.isPending ||
      revertStartMutation.isPending ||
      startMutation.isPending ||
      updateMutation.isPending,
    moveItem,
    removeItem,
    updateCadence,
    updateSprint,
  };
}
